import React, { useState, useEffect } from 'react';
import CoachResourcesService, { RESOURCE_CATEGORIES, RESOURCE_TYPES } from '../../services/coachResourcesService';
import {
  BookOpenIcon,
  DocumentTextIcon,
  PhoneIcon,
  LinkIcon,
  ClipboardDocumentListIcon,
  PlayIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  BuildingOfficeIcon,
  AcademicCapIcon,
  ShieldCheckIcon,
  ClockIcon,
  TagIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

const CoachResourcesPage = () => {
  const [resources, setResources] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [trainingResources, setTrainingResources] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSport, setSelectedSport] = useState('all');
  const [showRequiredOnly, setShowRequiredOnly] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [resourcesData, contactsData, trainingData, statsData] = await Promise.all([
        CoachResourcesService.getAllResources(),
        CoachResourcesService.getContacts(),
        CoachResourcesService.getTrainingResources(),
        CoachResourcesService.getResourceStatistics()
      ]);
      
      setResources(resourcesData);
      setContacts(contactsData);
      setTrainingResources(trainingData);
      setStatistics(statsData);
    } catch (error) {
      console.error('Error loading coach resources:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      loadData();
      return;
    }
    
    try {
      const searchResults = await CoachResourcesService.searchResources(searchQuery);
      setResources(searchResults);
    } catch (error) {
      console.error('Error searching resources:', error);
    }
  };

  const filteredResources = resources.filter(resource => {
    if (selectedCategory !== 'all' && resource.category !== selectedCategory) return false;
    if (selectedSport !== 'all' && resource.sport && resource.sport.toLowerCase() !== selectedSport) return false;
    if (showRequiredOnly && !resource.required) return false;
    return true;
  });

  const getTypeIcon = (type) => {
    const iconMap = {
      [RESOURCE_TYPES.DOCUMENT]: DocumentTextIcon,
      [RESOURCE_TYPES.LINK]: LinkIcon,
      [RESOURCE_TYPES.CONTACT]: PhoneIcon,
      [RESOURCE_TYPES.FORM]: ClipboardDocumentListIcon,
      [RESOURCE_TYPES.VIDEO]: PlayIcon,
      [RESOURCE_TYPES.GUIDE]: BookOpenIcon
    };
    return iconMap[type] || DocumentTextIcon;
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: BuildingOfficeIcon },
    { id: 'tssaa', name: 'TSSAA Resources', icon: ShieldCheckIcon },
    { id: 'dccsl', name: 'DCCSL Resources', icon: AcademicCapIcon },
    { id: 'training', name: 'Training & Certification', icon: AcademicCapIcon },
    { id: 'contacts', name: 'Important Contacts', icon: PhoneIcon }
  ];

  const sports = ['Football', 'Basketball', 'Baseball', 'Softball'];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading coach resources...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <BookOpenIcon className="h-16 w-16 mx-auto mb-4" />
            <h1 className="text-4xl font-bold mb-4">Coach Resources</h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Essential resources for East End Prep coaches including TSSAA and DCCSL information, 
              rules, regulations, training requirements, and important contacts.
            </p>
          </div>
          
          {/* Quick Stats */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold">{statistics.totalResources}</div>
              <div className="text-blue-200">Total Resources</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold">{statistics.requiredResources}</div>
              <div className="text-blue-200">Required Resources</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold">{statistics.contacts}</div>
              <div className="text-blue-200">Important Contacts</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold">{statistics.trainingResources}</div>
              <div className="text-blue-200">Training Resources</div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{tab.name}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Search and Filters */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Resource Search & Filters</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search resources..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                </div>
                
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Categories</option>
                  <option value={RESOURCE_CATEGORIES.TSSAA}>TSSAA</option>
                  <option value={RESOURCE_CATEGORIES.DCCSL}>DCCSL</option>
                  <option value={RESOURCE_CATEGORIES.RULES}>Rules</option>
                  <option value={RESOURCE_CATEGORIES.TRAINING}>Training</option>
                  <option value={RESOURCE_CATEGORIES.FORMS}>Forms</option>
                </select>
                
                <select
                  value={selectedSport}
                  onChange={(e) => setSelectedSport(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Sports</option>
                  {sports.map(sport => (
                    <option key={sport} value={sport.toLowerCase()}>{sport}</option>
                  ))}
                </select>
                
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={showRequiredOnly}
                    onChange={(e) => setShowRequiredOnly(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Required Only</span>
                </label>
              </div>
              
              <button
                onClick={handleSearch}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center space-x-2"
              >
                <MagnifyingGlassIcon className="h-4 w-4" />
                <span>Search</span>
              </button>
            </div>

            {/* Resource Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredResources.map((resource) => {
                const TypeIcon = getTypeIcon(resource.type);
                return (
                  <div key={resource.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow">
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <TypeIcon className="h-6 w-6 text-blue-600" />
                          <div>
                            <h3 className="font-semibold text-gray-900">{resource.title}</h3>
                            <p className="text-sm text-gray-500">{resource.category}</p>
                          </div>
                        </div>
                        {resource.required && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Required
                          </span>
                        )}
                      </div>
                      
                      <p className="text-gray-600 text-sm mb-4">{resource.description}</p>
                      
                      <div className="flex items-center justify-end space-x-3">
                        {resource.documentUrl && (
                          <a
                            href={resource.documentUrl}
                            download
                            className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                            Download
                          </a>
                        )}
                        {resource.url && (
                          <a
                            href={resource.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            View Resource →
                          </a>
                        )}
                      </div>
                      
                      {resource.lastUpdated && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <div className="flex items-center text-xs text-gray-500">
                            <ClockIcon className="h-4 w-4 mr-1" />
                            Updated: {CoachResourcesService.formatDate(resource.lastUpdated)}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'tssaa' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">TSSAA Resources</h2>
              <p className="text-gray-600 mb-6">
                Tennessee Secondary School Athletic Association resources for coaches and athletic programs.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {resources.filter(r => r.category === RESOURCE_CATEGORIES.TSSAA).map((resource) => {
                  const TypeIcon = getTypeIcon(resource.type);
                  return (
                    <div key={resource.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <TypeIcon className="h-6 w-6 text-blue-600 mt-1" />
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-2">{resource.title}</h3>
                          <p className="text-gray-600 text-sm mb-3">{resource.description}</p>
                          
                          {resource.tags && (
                            <div className="flex flex-wrap gap-1 mb-3">
                              {resource.tags.map((tag, index) => (
                                <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">
                                  <TagIcon className="h-3 w-3 mr-1" />
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                          
                          <div className="flex items-center justify-between">
                            {resource.required && (
                              <span className="inline-flex items-center text-xs text-red-600">
                                <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                                Required
                              </span>
                            )}
                            
                            <div className="flex items-center space-x-3">
                              {resource.documentUrl && (
                                <a
                                  href={resource.documentUrl}
                                  download
                                  className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium"
                                >
                                  <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                                  Download
                                </a>
                              )}
                              {resource.url && (
                                <a
                                  href={resource.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                >
                                  Access Resource →
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'dccsl' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">DCCSL Resources</h2>
              <p className="text-gray-600 mb-6">
                Davidson County Christian School League resources and information.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {resources.filter(r => r.category === RESOURCE_CATEGORIES.DCCSL).map((resource) => {
                  const TypeIcon = getTypeIcon(resource.type);
                  return (
                    <div key={resource.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <TypeIcon className="h-6 w-6 text-green-600 mt-1" />
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-2">{resource.title}</h3>
                          <p className="text-gray-600 text-sm mb-3">{resource.description}</p>
                          
                          <div className="flex items-center justify-between">
                            {resource.required && (
                              <span className="inline-flex items-center text-xs text-red-600">
                                <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                                Required
                              </span>
                            )}
                            
                            <div className="flex items-center space-x-3">
                              {resource.documentUrl && (
                                <a
                                  href={resource.documentUrl}
                                  download
                                  className="inline-flex items-center text-green-600 hover:text-green-800 text-sm font-medium"
                                >
                                  <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                                  Download
                                </a>
                              )}
                              {resource.url && (
                                <a
                                  href={resource.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-green-600 hover:text-green-800 text-sm font-medium"
                                >
                                  Access Resource →
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'training' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Training & Certification</h2>
              <p className="text-gray-600 mb-6">
                Required and recommended training courses for East End Prep coaches.
              </p>
              
              {trainingResources.map((resource) => (
                <div key={resource.id} className="border border-gray-200 rounded-lg p-6 mb-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">{resource.title}</h3>
                      <p className="text-gray-600">{resource.description}</p>
                    </div>
                    {resource.required && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                        <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                        Required
                      </span>
                    )}
                  </div>
                  
                  {resource.courses && (
                    <div className="space-y-3">
                      <h4 className="font-medium text-gray-900">Required Courses:</h4>
                      {resource.courses.map((course, index) => (
                        <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                          <CheckCircleIcon className={`h-5 w-5 mt-0.5 ${
                            course.required ? 'text-red-500' : 'text-green-500'
                          }`} />
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <h5 className="font-medium text-gray-900">{course.name}</h5>
                              <span className="text-sm text-gray-500">{course.duration}</span>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">{course.description}</p>
                            {course.required && (
                              <span className="inline-flex items-center text-xs text-red-600 mt-1">
                                Required for all coaches
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {resource.provider && (
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-800">
                        <strong>Provider:</strong> {resource.provider}
                        {resource.renewalPeriod && (
                          <span className="ml-4">
                            <strong>Renewal:</strong> Every {resource.renewalPeriod}
                          </span>
                        )}
                      </p>
                    </div>
                  )}
                  
                  {resource.url && (
                    <div className="mt-4">
                      <a
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                      >
                        <LinkIcon className="h-4 w-4 mr-2" />
                        Access Training Platform
                      </a>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'contacts' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Important Contacts</h2>
              <p className="text-gray-600 mb-6">
                Key contacts for coaching support, league information, and administrative assistance.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {contacts.map((contact) => (
                  <div key={contact.id} className="border border-gray-200 rounded-lg p-6">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                          <PhoneIcon className="h-6 w-6 text-blue-600" />
                        </div>
                      </div>
                      
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">{contact.title}</h3>
                        <p className="text-sm text-gray-500 mb-3">{contact.organization}</p>
                        
                        <div className="space-y-2">
                          {contact.contactName && (
                            <p className="text-sm">
                              <strong>Contact:</strong> {contact.contactName}
                              {contact.contactTitle && ` - ${contact.contactTitle}`}
                            </p>
                          )}
                          
                          {contact.phone && (
                            <p className="text-sm">
                              <strong>Phone:</strong> 
                              <a href={`tel:${contact.phone}`} className="text-blue-600 hover:text-blue-800 ml-1">
                                {contact.phone}
                              </a>
                            </p>
                          )}
                          
                          {contact.email && (
                            <p className="text-sm">
                              <strong>Email:</strong> 
                              <a href={`mailto:${contact.email}`} className="text-blue-600 hover:text-blue-800 ml-1">
                                {contact.email}
                              </a>
                            </p>
                          )}
                          
                          {contact.address && (
                            <p className="text-sm">
                              <strong>Address:</strong> {contact.address}
                            </p>
                          )}
                          
                          {contact.office && (
                            <p className="text-sm">
                              <strong>Office:</strong> {contact.office}
                            </p>
                          )}
                        </div>
                        
                        {contact.hours && (
                          <div className="mt-3 p-2 bg-gray-50 rounded">
                            <p className="text-xs text-gray-600">
                              <ClockIcon className="h-4 w-4 inline mr-1" />
                              {contact.hours}
                            </p>
                          </div>
                        )}
                        
                        {contact.services && (
                          <div className="mt-3">
                            <p className="text-sm font-medium text-gray-900 mb-1">Services:</p>
                            <ul className="text-sm text-gray-600 space-y-1">
                              {contact.services.map((service, index) => (
                                <li key={index} className="flex items-center">
                                  <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" />
                                  {service}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CoachResourcesPage;